import { useEffect, useRef } from 'react'
import {
  Badge,
  Button,
  Caption1,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowLeft20Regular, ArrowRight20Regular, Dismiss20Regular, Open20Regular } from '@fluentui/react-icons'
import type { Demand } from '../types/demand'
import { focusAreaWithName } from '../types/focus_area'
import { STAGE_LABELS, priorityTierLabel } from '../lib/labels'
import { formatRelative } from '../lib/relativeTime'

const useStyles = makeStyles({
  drawer: { width: '480px', maxWidth: '100vw' },
  header: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, paddingBottom: tokens.spacingVerticalM },
  body: { paddingTop: tokens.spacingVerticalL },
  title: {
    fontSize: tokens.fontSizeBase600,
    lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightSemibold,
    margin: `0 0 ${tokens.spacingVerticalM}`,
    overflowWrap: 'anywhere',
  },
  metadata: { color: tokens.colorNeutralForeground3 },
  badges: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalL },
  facts: {
    display: 'grid', gridTemplateColumns: '100px minmax(0, 1fr)',
    gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    margin: `0 0 ${tokens.spacingVerticalXL}`,
    '& dt': { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
    '& dd': { margin: 0, overflowWrap: 'anywhere' },
  },
  section: { marginBottom: tokens.spacingVerticalXL },
  sectionTitle: { fontSize: tokens.fontSizeBase300, margin: `0 0 ${tokens.spacingVerticalS}`, fontWeight: tokens.fontWeightSemibold },
  description: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: tokens.lineHeightBase400, margin: 0 },
  footer: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  navigation: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
})

interface DemandPreviewDrawerProps {
  demand: Demand
  index: number
  count: number
  onClose: () => void
  onPrevious: () => void
  onNext: () => void
  onOpenDetails: () => void
}

/** Read-only preview of the already-authorized list response; no detail-only
 * queries or editing controls are mounted while someone is just browsing. */
export function DemandPreviewDrawer({
  demand, index, count, onClose, onPrevious, onNext, onOpenDetails,
}: DemandPreviewDrawerProps) {
  const styles = useStyles()
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0
  }, [demand.id])

  return (
    <OverlayDrawer
      open
      position="end"
      size="medium"
      className={styles.drawer}
      aria-label="Demand preview"
      onOpenChange={(_, data) => { if (!data.open) onClose() }}
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle action={
          <Button appearance="subtle" icon={<Dismiss20Regular />} aria-label="Close preview" onClick={onClose} />
        }>
          Quick preview
        </DrawerHeaderTitle>
        <Caption1 className={styles.metadata}>{demand.demand_id ?? `#${demand.id}`}</Caption1>
      </DrawerHeader>
      <DrawerBody ref={bodyRef} className={styles.body}>
        <h2 className={styles.title} aria-live="polite">{demand.title}</h2>
        <div className={styles.badges}>
          <Badge appearance="tint" color="informative">
            {demand.disposition === 'digital_council' ? 'Routed to DSAI' : STAGE_LABELS[demand.stage]}
          </Badge>
          {demand.priority_bucket && <Badge appearance="outline">{priorityTierLabel(demand.priority_bucket)}</Badge>}
          {demand.deleted_at && <Badge appearance="tint" color="danger">Deleted</Badge>}
        </div>
        <dl className={styles.facts}>
          <dt>Focus area</dt><dd>{focusAreaWithName(demand.focus_area_code)}</dd>
          <dt>Caretaker</dt><dd>{demand.caretaker_name ?? 'Unassigned'}</dd>
          <dt>Requestor</dt><dd>{demand.requestor_name || 'Not specified'}</dd>
          <dt>Last activity</dt><dd>{formatRelative(demand.last_activity_at ?? demand.created_at, 'Not available')}</dd>
        </dl>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Description</h3>
          <p className={styles.description}>{demand.description?.trim() || 'No description yet.'}</p>
        </section>
        {!!demand.skills?.length && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Skills</h3>
            <p className={styles.description}>{demand.skills.join(' · ')}</p>
          </section>
        )}
        {!!demand.tags?.length && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Tags</h3>
            <div className={styles.badges}>
              {demand.tags.map((tag) => <Badge key={tag.id} appearance="outline">{tag.label}</Badge>)}
            </div>
          </section>
        )}
      </DrawerBody>
      <DrawerFooter className={styles.footer}>
        <div className={styles.navigation}>
          <Button appearance="subtle" icon={<ArrowLeft20Regular />} aria-label="Previous demand" disabledFocusable={index === 0} onClick={onPrevious} />
          <Caption1 aria-live="polite">{index + 1} of {count} on this page</Caption1>
          <Button appearance="subtle" icon={<ArrowRight20Regular />} aria-label="Next demand" disabledFocusable={index === count - 1} onClick={onNext} />
        </div>
        <Button appearance="primary" icon={<Open20Regular />} onClick={onOpenDetails}>Open details</Button>
      </DrawerFooter>
    </OverlayDrawer>
  )
}
